import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { MESSAGES } from "@/constants";
import { useEvaluateContext, useEvaluateLiveSet } from "@/hooks/useContextConsole";
import {
  apiErrorMessage,
  type EvaluateResponse,
  type PickedContextFiles,
} from "@/services/apis/contextConsoleAPI";
import {
  useCompletionToast,
  useResetOnIdentityChange,
  type CompletionToastOptions,
} from "./providerHooks";

const NOTIFY = MESSAGES.CONTEXT_CONSOLE.NOTIFY.EVALUATE;
const VIEW_LABEL = MESSAGES.CONTEXT_CONSOLE.NOTIFY.VIEW;
const COPY = MESSAGES.CONTEXT_CONSOLE;

interface FileEvaluationContextValue {
  evaluation: EvaluateResponse | null;
  /** The exact `File` objects the current `evaluation` was scored from — held
   * here (not in the page's own state) so they survive navigating away and
   * back within the app, letting the admin publish without re-picking. Only a
   * hard refresh loses them: browsers don't let a page recover real file
   * handles across a reload without the OS picker, so that limitation is
   * unavoidable and unrelated to this. */
  files: PickedContextFiles | null;
  isPending: boolean;
  /** The raw error from the last failed attempt — callers that need to read
   * a specific payload off it (e.g. `missingFilesPayload`) can, same as they
   * would off a local mutation's `.error`. */
  error: unknown;
  /** True once the admin has actually seen this evaluation's result — same
   * contract as EvaluationJobContext: closing the notification does not set
   * this, only clicking through or seeing it render inline does. */
  hasBeenViewed: boolean;
  runEvaluate: (files: PickedContextFiles) => void;
  markViewed: () => void;
  /** Clear everything (e.g. "Start over", or picking a new file). */
  reset: () => void;
  /**
   * CC-B2's deferred score backfill (`POST /context/live-set/evaluate`) —
   * scores whatever's ALREADY live, no files of its own. Kept as its own
   * result/viewed pair, not folded into `evaluation`/`hasBeenViewed`: the
   * two actions are temporally exclusive in practice, but conflating them
   * would still make one silently overwrite the other's "seen" state.
   */
  liveSetEvaluation: EvaluateResponse | null;
  isPendingLiveSet: boolean;
  /** The raw error from the last failed live-set backfill — same contract as
   * `error` above. `notifyLiveSet` skips its toast while on the Context
   * Console page, so this is the ONLY signal a caller there has that the
   * backfill failed; a caller off-page already gets the toast instead. */
  liveSetError: unknown;
  hasBeenViewedLiveSet: boolean;
  runEvaluateLiveSet: () => void;
  markViewedLiveSet: () => void;
  /** When each of `evaluation`/`liveSetEvaluation` was last submitted (ms,
   * TanStack's own `submittedAt`) — lets a caller showing both prefer
   * whichever action is actually the most recent, instead of a fixed
   * precedence that can leave a stale evaluate masking a fresher live-set
   * backfill the admin just explicitly asked to see (or vice versa). 0
   * until that mutation has ever been submitted. */
  evaluationSubmittedAt: number;
  liveSetSubmittedAt: number;
}

const FileEvaluationContext = createContext<FileEvaluationContextValue | undefined>(undefined);

/** What to tell the admin when an evaluate settles while they're elsewhere. */
const evaluateToast = (
  result: EvaluateResponse | null,
  failMessage: string | null,
  onView: () => void,
): CompletionToastOptions => ({
  toastId: "file-evaluation",
  success: result !== null,
  title: result ? NOTIFY.TITLE : NOTIFY.FAILED_TITLE,
  description: result
    ? NOTIFY.BODY(Math.round(result.combined_score))
    : failMessage || NOTIFY.FAILED_BODY,
  viewLabel: VIEW_LABEL,
  onView,
});

/** The same, for a live-set backfill. */
const liveSetToast = (
  result: EvaluateResponse | null,
  failMessage: string | null,
  onView: () => void,
): CompletionToastOptions => ({
  toastId: "live-set-evaluation",
  success: result !== null,
  title: result ? COPY.EVALUATION_BACKFILLED_TITLE : COPY.EVALUATION_BACKFILL_FAILED_TITLE,
  description: result
    ? COPY.EVALUATION_BACKFILLED_BODY(Math.round(result.combined_score))
    : failMessage || COPY.EVALUATION_BACKFILL_FAILED,
  viewLabel: VIEW_LABEL,
  onView,
});

/**
 * The evaluate-on-staged-files half of the provider: the mutation, its result
 * and "seen" flag, and the off-page completion toast.
 *
 * `mutate`/`reset` are stable refs from TanStack — destructured so the
 * callbacks can list them as deps without re-creating on every render the way
 * depending on the whole (freshly-allocated) `mutation` object would. The
 * result is read straight off the mutation rather than copied into state:
 * TanStack already guarantees the hook reflects only the LATEST `mutate` call,
 * and that per-call `onSuccess`/`onError` fire only for that latest call — so a
 * superseded evaluate can neither clobber a newer result nor fire a stale toast,
 * with no attempt counter of our own.
 */
const useEvaluateFlow = () => {
  const mutation = useEvaluateContext();
  const { mutate, reset: resetMutation } = mutation;
  const [hasBeenViewed, setHasBeenViewed] = useState(false);
  const showCompletionToast = useCompletionToast();
  const markViewed = useCallback(() => setHasBeenViewed(true), []);

  const notify = useCallback(
    (result: EvaluateResponse | null, failMessage: string | null) =>
      showCompletionToast(evaluateToast(result, failMessage, markViewed)),
    [showCompletionToast, markViewed],
  );

  const runEvaluate = useCallback(
    (picked: PickedContextFiles) => {
      mutate(picked, {
        onSuccess: (result) => {
          setHasBeenViewed(false);
          notify(result, null);
        },
        onError: (err) => {
          setHasBeenViewed(false);
          // apiErrorMessage, not err.message: err is an AxiosError, whose own
          // .message is a generic "Request failed with status code 400" — the
          // backend's actual, admin-actionable text lives in
          // err.response.data.message.
          notify(null, apiErrorMessage(err));
        },
      });
    },
    [mutate, notify],
  );

  const reset = useCallback(() => {
    resetMutation();
    setHasBeenViewed(false);
  }, [resetMutation]);

  return {
    evaluation: mutation.data ?? null,
    files: mutation.variables ?? null,
    isPending: mutation.isPending,
    error: mutation.error,
    submittedAt: mutation.submittedAt,
    hasBeenViewed,
    runEvaluate,
    markViewed,
    reset,
  };
};

/**
 * The live-set score backfill half of the provider (CC-B2): the same shape as
 * {@link useEvaluateFlow}, kept as its own mutation/result/"seen" trio.
 */
const useLiveSetFlow = () => {
  const mutation = useEvaluateLiveSet();
  const { mutate, reset: resetMutation } = mutation;
  const [hasBeenViewed, setHasBeenViewed] = useState(false);
  const showCompletionToast = useCompletionToast();
  const markViewed = useCallback(() => setHasBeenViewed(true), []);

  const notify = useCallback(
    (result: EvaluateResponse | null, failMessage: string | null) =>
      showCompletionToast(liveSetToast(result, failMessage, markViewed)),
    [showCompletionToast, markViewed],
  );

  const runEvaluateLiveSet = useCallback(() => {
    mutate(undefined, {
      onSuccess: (result) => {
        setHasBeenViewed(false);
        notify(result, null);
      },
      onError: (err) => {
        setHasBeenViewed(false);
        notify(null, apiErrorMessage(err));
      },
    });
  }, [mutate, notify]);

  const reset = useCallback(() => {
    resetMutation();
    setHasBeenViewed(false);
  }, [resetMutation]);

  return {
    evaluation: mutation.data ?? null,
    isPending: mutation.isPending,
    error: mutation.error,
    submittedAt: mutation.submittedAt,
    hasBeenViewed,
    runEvaluate: runEvaluateLiveSet,
    markViewed,
    reset,
  };
};

/**
 * Lifts file-evaluation's request lifecycle above the router, mirroring
 * EvaluationJobContext — but simpler: `/context/evaluate` is one plain
 * request/response, not a server-tracked background job, so there is no
 * polling, no job id, and no sessionStorage persistence. A hard refresh
 * genuinely loses an in-flight evaluate (the browser aborts it, same as it
 * always would); only navigating within the app survives here, because the
 * mutation instance itself now lives in a provider mounted above the router
 * instead of inside the page that used to own it.
 */
export const FileEvaluationProvider = ({
  children,
  // See EvaluationJobProvider's identityKey default — same reasoning.
  identityKey = "anon",
}: {
  children: ReactNode;
  /** The signed-in user this instance belongs to — see ContextConsoleProviders.tsx
   * and EvaluationJobContext's identity-change effect for the full reasoning.
   * This provider has no sessionStorage to worry about, but the in-memory
   * `evaluation`/`files` (real picked File objects, including their bytes)
   * need the same same-tab-account-switch defense. */
  identityKey?: string;
}) => {
  const evaluate = useEvaluateFlow();
  const liveSet = useLiveSetFlow();

  const { reset: resetEvaluate } = evaluate;
  const { reset: resetLiveSet } = liveSet;
  // Detaches anything still in flight AND clears data/variables/error, so every
  // field this context exposes goes back to its empty state.
  const reset = useCallback(() => {
    resetEvaluate();
    resetLiveSet();
  }, [resetEvaluate, resetLiveSet]);
  useResetOnIdentityChange(identityKey, reset);

  const value = useMemo(
    () => ({
      evaluation: evaluate.evaluation,
      files: evaluate.files,
      isPending: evaluate.isPending,
      error: evaluate.error,
      hasBeenViewed: evaluate.hasBeenViewed,
      runEvaluate: evaluate.runEvaluate,
      markViewed: evaluate.markViewed,
      reset,
      liveSetEvaluation: liveSet.evaluation,
      isPendingLiveSet: liveSet.isPending,
      liveSetError: liveSet.error,
      hasBeenViewedLiveSet: liveSet.hasBeenViewed,
      runEvaluateLiveSet: liveSet.runEvaluate,
      markViewedLiveSet: liveSet.markViewed,
      evaluationSubmittedAt: evaluate.submittedAt,
      liveSetSubmittedAt: liveSet.submittedAt,
    }),
    [
      evaluate.evaluation, evaluate.files, evaluate.isPending, evaluate.error, evaluate.hasBeenViewed,
      evaluate.runEvaluate, evaluate.markViewed, reset, evaluate.submittedAt,
      liveSet.evaluation, liveSet.isPending, liveSet.error, liveSet.hasBeenViewed,
      liveSet.runEvaluate, liveSet.markViewed, liveSet.submittedAt,
    ],
  );

  return <FileEvaluationContext.Provider value={value}>{children}</FileEvaluationContext.Provider>;
};

export const useFileEvaluation = (): FileEvaluationContextValue => {
  const context = useContext(FileEvaluationContext);
  if (context === undefined) {
    throw new Error("useFileEvaluation must be used within a FileEvaluationProvider");
  }
  return context;
};
